"use client";

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { authService } from "../../apis/authService";
import { GoogleLoginButton } from "../ui/google-login-button";

export function SignUpForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const criteria = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "One number", test: (p) => /[0-9]/.test(p) },
    { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];

  const passedCount = criteria.filter((c) => c.test(formData.password)).length;

  const strengthConfig = [
    { label: "", color: "bg-gray-200" },
    { label: "Weak", color: "bg-red-400" },
    { label: "Fair", color: "bg-orange-400" },
    { label: "Good", color: "bg-yellow-400" },
    { label: "Strong", color: "bg-green-500" },
  ];
  const { label: strengthLabel, color: strengthColor } = strengthConfig[passedCount];

  const getFormErrors = (data) => {
    const newErrors = {};
    const email = (data.email || "").trim();
    const passwordPassed = criteria.filter((c) => c.test(data.password || "")).length;

    if (!email) newErrors.email = "Email required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Invalid email format";
    if (!data.password) newErrors.password = "Password required";
    else if (passwordPassed !== 4) newErrors.password = "Please meet all password requirements";
    if (data.password !== data.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (!data.acceptTerms) newErrors.acceptTerms = "Accept terms to continue";
    return newErrors;
  };

  const applyFormUpdate = (updates) => {
    const next = { ...formData, ...updates };
    setFormData(next);
    setErrors((prev) => (Object.keys(prev).length ? getFormErrors(next) : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = getFormErrors(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await authService.signup({
        email: formData.email,
        password: formData.password,
      });

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        setRegisteredUserId(result.userId);
        setIsEmailSent(true);
        setCountdown(60); // Start 60s countdown for first resend
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch {
      setMessage({ type: "error", text: "Signup failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (user) => {
    setMessage({ type: "success", text: `Welcome ${user.name}!` });
    setTimeout(() => navigate("/scribe"), 1500);
  };

  const handleGoogleError = (error) => {
    setMessage({ type: "error", text: error });
  };

  const handleResendEmail = async () => {
    if (countdown > 0 || isResending) return;
    setIsResending(true);
    setMessage(null);
    try {
      const res = await authService.resendOtp(registeredUserId || formData.email, "signup");
      if (res.success) {
        setMessage({ type: "success", text: "Confirmation email resent!" });
        setCountdown(60);
      } else {
        setMessage({ type: "error", text: res.message || "Failed to resend confirmation email." });
      }
    } catch {
      setMessage({ type: "error", text: "Failed to resend confirmation email." });
    } finally {
      setIsResending(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="space-y-6 text-center py-6">
        <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-100 shadow-sm">
          <Mail size={32} className="animate-pulse" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Check Your Inbox</h3>
        <p className="text-base text-gray-600 leading-relaxed max-w-sm mx-auto">
          We've sent a welcome email to <strong className="text-blue-600 font-semibold">{formData.email}</strong>.
          Please click the confirmation link in the email to verify your email address and activate your account.
        </p>
        <div className="pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-400 mb-4">
            Didn't receive the email? Check your spam/junk or promotions tab.
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleResendEmail}
              disabled={isResending || countdown > 0}
              className={`w-full h-11 border rounded-lg font-medium text-sm transition duration-200 ${countdown > 0
                ? "bg-slate-50 text-gray-400 border-gray-200 cursor-not-allowed"
                : "border-blue-500 text-blue-600 hover:bg-blue-50 cursor-pointer"
                }`}
            >
              {isResending ? "Resending..." : countdown > 0 ? `Resend Link again in ${countdown}s` : "Resend Welcome Email"}
            </button>
            <Link
              to="/signin"
              className="text-sm text-gray-500 hover:text-blue-600 mt-2 font-medium"
            >
              Back to Sign In
            </Link>
          </div>
        </div>
        {message && (
          <div className={`p-3 rounded-lg flex items-center gap-2 mt-4 text-left ${message.type === "success" ? "bg-teal-50 text-teal-600 border border-teal-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {message.type === "success" ? <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {message && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${message.type === "success" ? "bg-teal-50 text-teal-600 border border-teal-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      <GoogleLoginButton text="Sign up with Google" onSuccess={handleGoogleSuccess} onError={handleGoogleError} disabled={isLoading} />

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-base text-gray-400">or</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Email */}
        <div>
          <div className="relative flex items-center">
            <Mail size={20} className="absolute left-4 text-gray-400 pointer-events-none" />
            <input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => applyFormUpdate({ email: e.target.value })}
              className="w-full h-12 pl-12 pr-4 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg text-slate-900 placeholder:text-gray-400"
            />
          </div>
          {errors.email && <p className="text-sm text-red-500 mt-1.5">{errors.email}</p>}
        </div>

        {/* Password & Confirm side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="relative flex items-center">
              <Lock size={20} className="absolute left-4 text-gray-400 pointer-events-none" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={formData.password}
                onChange={(e) => applyFormUpdate({ password: e.target.value })}
                className="w-full h-12 pl-12 pr-12 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg text-slate-900 placeholder:text-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center p-0"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-500 mt-1.5">{errors.password}</p>}
          </div>

          <div>
            <div className="relative flex items-center">
              <Lock size={20} className="absolute left-4 text-gray-400 pointer-events-none" />
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm"
                value={formData.confirmPassword}
                onChange={(e) => applyFormUpdate({ confirmPassword: e.target.value })}
                className={`w-full h-12 pl-12 pr-12 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg text-slate-900 placeholder:text-gray-400 transition ${formData.confirmPassword && formData.confirmPassword !== formData.password
                  ? "border-red-300"
                  : "border-gray-300"
                  }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center p-0"
                aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-sm text-red-500 mt-1.5">{errors.confirmPassword}</p>}
            {formData.confirmPassword && formData.confirmPassword !== formData.password && !errors.confirmPassword && (
              <p className="text-sm text-red-400 mt-1.5">Passwords do not match</p>
            )}
          </div>
        </div>

        {/* Strength meter and requirements checklist (only when password is not empty) */}
        {formData.password && (
          <div className="space-y-3">
            <div>
              <div className="flex gap-1.5 items-center">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${i <= passedCount ? strengthColor : "bg-gray-200"
                      }`}
                  />
                ))}
                <span
                  className={`text-sm font-semibold ml-1 whitespace-nowrap ${passedCount === 4
                    ? "text-green-600"
                    : passedCount >= 3
                      ? "text-yellow-500"
                      : passedCount >= 2
                        ? "text-orange-400"
                        : "text-red-400"
                    }`}
                >
                  {strengthLabel}
                </span>
              </div>
            </div>

            {/* Criteria checklist */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {criteria.map((c) => {
                const ok = c.test(formData.password);
                return (
                  <div key={c.label} className="flex items-center gap-1.5">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? "bg-green-500" : "bg-gray-200"
                        }`}
                    >
                      {ok ? (
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                          <path
                            d="M2 5l2.5 2.5L8 3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg className="w-2.5 h-2.5 text-gray-400" fill="none" viewBox="0 0 10 10">
                          <path
                            d="M3 3l4 4M7 3L3 7"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm leading-tight ${ok ? "text-gray-700" : "text-gray-400"}`}>
                      {c.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Terms */}
        <div className="pt-2">
          <label className="flex items-start gap-2 text-base text-gray-600">
            <input
              type="checkbox"
              checked={formData.acceptTerms}
              onChange={(e) => applyFormUpdate({ acceptTerms: e.target.checked })}
              className="w-4.5 h-4.5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
            />
            <span>
              I agree to the{" "}
              <a
                href="https://virtualscribe.ai/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="https://virtualscribe.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Privacy Policy
              </a>
            </span>
          </label>
          {errors.acceptTerms && <p className="text-sm text-red-500 mt-1.5">{errors.acceptTerms}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-[52px] rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white font-medium text-base transition disabled:opacity-70"
        >
          {isLoading ? "Creating account..." : "Create Account"}
        </button>
      </form>

      <p className="text-center text-base text-gray-500 mt-5">
        Already have an account? <Link to="/signin" className="text-blue-600 font-medium hover:underline">Sign in here</Link>
      </p>
    </div>
  );
}